import { useState, useEffect, useMemo } from "react";
import API from "@/services/api";
import toast from "react-hot-toast";

export const useDistribution = (job, posts, setPosts) => {
  const [activeChannels, setActiveChannels] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [marketChannels, setMarketChannels] = useState([]); // Raw market channels

  const [loading, setLoading] = useState({
    active: true,
    recommendations: true,
    market: false,
  });

  // 1. Fetch Active Channels
  useEffect(() => {
    const fetchActive = async () => {
      const activeIds = posts.filter((p) => p.status === "active").map((p) => p.channel_id);

      if (activeIds.length === 0) {
        setActiveChannels([]);
        setLoading((prev) => ({ ...prev, active: false }));
        return;
      }

      try {
        const { data } = await API.post("/channel/search", { limit: 1000 });
        if (data) {
          setActiveChannels(data.filter((ch) => activeIds.includes(ch._id)));
          console.log(
            "activeChannels",
            data.filter((ch) => activeIds.includes(ch._id))
          );
        }
      } catch (err) {
        console.error("Failed to load active channels", err);
      } finally {
        setLoading((prev) => ({ ...prev, active: false }));
      }
    };

    fetchActive();
  }, [posts]);

  // 2. Fetch Recommendations
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!job?._id) {
        setLoading((prev) => ({ ...prev, recommendations: false }));
        return;
      }

      try {
        const { data } = await API.post("/channel/recommendations", {
          job_id: job._id,
          language: "en",
        });

        if (data?.rankedPlatforms) {
          setRecommendations(data.rankedPlatforms.map((r) => ({ ...r, isRecommended: true })));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading((prev) => ({ ...prev, recommendations: false }));
      }
    };

    // Reset loading state when job changes
    setLoading((prev) => ({ ...prev, recommendations: true }));
    fetchRecommendations();
  }, [job?._id]);

  // 3. Search / Browse Marketplace
  const searchMarketplace = async (query = "") => {
    setLoading((prev) => ({ ...prev, market: true }));
    try {
      // If query is empty, we want to see all internal channels.
      // The backend should handle empty search by returning default list.
      const { data } = await API.post("/channel/search", {
        search: query,
        limit: 50,
      });

      if (Array.isArray(data)) {
        setMarketChannels(data);
      } else if (data && Array.isArray(data.data)) {
        // Handle case where backend returns { ok: true, data: [...] } wrapped or similar
        setMarketChannels(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, market: false }));
    }
  };

  // 4. Derived State: Available Channels
  // Filter out active channels from the market list to avoid duplicates
  const availableChannels = useMemo(() => {
    const activeIds = activeChannels.map((c) => c._id);
    return marketChannels.filter((c) => !activeIds.includes(c._id));
  }, [marketChannels, activeChannels]);

  // 5. Action: Activate a Channel
  const activateChannel = async (channel) => {
    let channelId = channel._id;
    let channelData = channel;

    // 1. Create Channel if it doesn't exist (e.g. from recommendations)
    if (!channelId) {
      try {
        const { data: newChannel } = await API.post("/channel", {
          name: channel.name,
          url: channel.url,
          description: channel.whyPostHere || "",
          published: true,
          logo: channel.logo,
        });
        channelId = newChannel._id;
        channelData = newChannel;
      } catch (err) {
        toast.error("Failed to create channel");
        return;
      }
    }

    // 2. Check if already active
    if (posts.find((p) => p.channel_id === channelId)) {
      toast.success("Channel is already active");
      return;
    }

    // 3. Create Post & Update UI
    try {
      const res = await API.post("/post", {
        job_id: job._id,
        channel_id: channelId,
      });
      if (!res.ok) {
        if (res.code === "CHANNEL_ALREADY_ACTIVE") {
          toast.error("This channel is already active for this job");
          return;
        }
        toast.error(res.code || "Failed to activate channel");
        return;
      }

      const newPost = res.data;

      // Update parent state (triggers the "Active" list refresh)
      setPosts([...posts, { ...newPost, status: "active" }]);

      // Optimistically add to active list to make it feel instant
      if (!activeChannels.find((c) => c._id === channelId)) {
        setActiveChannels((prev) => [...prev, channelData]);
      }

      toast.success("Channel activated");
    } catch (e) {
      console.error(e);
      toast.error(e?.code || "Failed to activate channel");
    }
  };

  return {
    activeChannels,
    recommendations,
    availableChannels,
    loading,
    searchMarketplace,
    activateChannel,
  };
};
